import { Trader } from "./trader";
import traderService from "./traderService";
import Event, { EventAction } from "common/event";
import { ActionWithDispatch } from "common/actionWithDispatch";
import TraderConfiguration from "./traderConfiguration";
import { trackApiCallNew } from "features/saveIndicator/trackApiCall";
import { Dispatch } from "redux";
import UserGroup from "domain/userGroup";
import { forEach } from "lodash";

export class TradersLoaded extends Event {
  constructor(public traders: Trader[]) {
    super(TradersLoaded.EventName);
  }

  public static EventName: string = "TRADERS_LOADED";
}

export class TraderGroupsLoaded extends Event {
  constructor(public traderGroups: UserGroup[]) {
    super(TraderGroupsLoaded.EventName);
  }

  public static EventName: string = "TRADER_GROUPS_LOADED";
}

export class TraderConfigurationLoaded extends Event {
  constructor(public configuration: TraderConfiguration) {
    super(TraderConfigurationLoaded.EventName);
  }

  public static EventName: string = "TRADER_CONFIGURATION_LOADED";
}

export class TraderAdded extends Event {
  constructor(public trader: Trader) {
    super(TraderAdded.EventName);
  }

  public static EventName: string = "TRADER_ADDED";
}

export class TraderUpdated extends Event {
  constructor(public trader: Trader) {
    super(TraderUpdated.EventName);
  }

  public static EventName: string = "TRADER_UPDATED";
}

export class TraderDeleted extends Event {
  constructor(public trader: Trader) {
    super(TraderDeleted.EventName);
  }

  public static EventName: string = "TRADER_DELETED";
}

export function loadTraders(): ActionWithDispatch<TradersLoaded> {
  return function (dispatch) {
    return traderService
      .listTraders()
      .then(traderSettings =>
        dispatch(new TradersLoaded(traderSettings).toObject())
      );
  };
}

export function loadTraderConfiguration(dispatch: Dispatch): Promise<EventAction<TraderConfigurationLoaded>> {
  return traderService
    .getTraderConfiguration()
    .then(data => dispatch(new TraderConfigurationLoaded(data).toObject()));
}

export function createTrader(dispatch: Dispatch, trader: Trader): Promise<any> {
  return trackApiCallNew(dispatch, async innerDispatch => {
    const newTrader = await traderService.createTrader(trader)

    await onTraderGroupChanged(trader, innerDispatch);
    innerDispatch(new TraderAdded(newTrader).toObject())
  });
}

export function updateTrader(dispatch: Dispatch, newValue: Trader, oldValue: Trader): Promise<any> {
  return trackApiCallNew(dispatch, async innerDispatch => {
    await traderService
      .updateTrader(newValue)
      .catch((error) => {
        dispatch(new TraderUpdated(oldValue).toObject());
        throw error;
      });

    await onTraderGroupChanged(newValue, innerDispatch);
    innerDispatch(new TraderUpdated(newValue).toObject());
  });
}

async function onTraderGroupChanged(trader: Trader, innerDispatch: any) {
  if (trader.traderGroups?.some(m => !m.id)) {
    const traderGroups = await traderService.listTraderGroups();
    innerDispatch(new TraderGroupsLoaded(traderGroups).toObject());

    forEach(trader.traderGroups, (group) => {
      if (!group.id) {
        const foundGroup = traderGroups.find(m => m.name === group.name);
        if (foundGroup) {
          group.id = foundGroup.id;
        }
      }
    });
  }
}

export function updateTraderCellPhone(dispatch: Dispatch, newValue: Trader, oldValue: Trader): Promise<any> {
  return trackApiCallNew(dispatch, innerDispatch => {
    innerDispatch(new TraderUpdated(newValue).toObject());
    return traderService
      .updateTraderCellPhone(newValue)
      .catch((error) => {
        dispatch(new TraderUpdated(oldValue).toObject());
        throw error;
      });
  });
}

export function deleteTrader(dispatch: Dispatch, trader: Trader): Promise<any> {
  return trackApiCallNew(dispatch, innerDispatch =>
    traderService.deleteTrader(trader).then(() => innerDispatch(new TraderDeleted(trader).toObject()))
  );
}

export function loadTraderGroups(): ActionWithDispatch<TraderGroupsLoaded> {
  return function (dispatch) {
    return traderService
      .listTraderGroups()
      .then(traderSettings =>
        dispatch(new TraderGroupsLoaded(traderSettings).toObject())
      );
  };
}
